import {
  Banner,
  Button, ListItem,
  SheetClose,
  SheetContentWrapper, SheetFooter,
  SheetHeader, space, SpaceBetween, tok,
  Typography,
} from '@sunwebgroup/ui-components';
import * as React from 'react';
import { styled } from 'styled-components';
import { useService } from '@/scopes/checkout/domain/hooks';
import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types';
import { useCarRentalServiceStore, editingCar } from '@/scopes/checkout/components/services/CarRentalFilteringService/store';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { useIntl } from '@/intl';
import { Icon, InputStepper, Switch } from '@sunwebgroup/ui-components/uds';
import { createCarEvent, createEquipmentSelectionEvent } from '@/scopes/checkout/domain/analytics';
import { editCar } from '@/scopes/checkout/domain/actions';
import {
  getTotalPriceForEquipmentsSelected
} from '@/scopes/checkout/components/services/CarRentalFilteringService/selectors';

export const getOptionEquipments = (option: CheckoutCarRentalServiceOption | undefined, bookedEquipmentsWithQuantity: {
  id: string;
  quantity: number;
}[] | undefined) => {
  if (!option) return [];
  const {equipments, maxNumberOfEquipments} = option
  const equipmentsLimit = maxNumberOfEquipments ?? equipments.length
  return equipments?.map((equipment) => {
    const bookedEquipment = bookedEquipmentsWithQuantity?.find((bookedEquipmentWithQuantity) => bookedEquipmentWithQuantity.id === equipment.id)
    return ({
      ...equipment,
      selected: Boolean(bookedEquipment),
      quantitySelected: bookedEquipment?.quantity ?? 0,
      isElegible: Boolean(bookedEquipmentsWithQuantity?.find(({id}) => id === equipment.id) ||  ((bookedEquipmentsWithQuantity?.length || 0) < equipmentsLimit))
    })
  }) ?? [];
}

export const EquipmentsForm = ({showEquipmentsForm, setShowEquipmentsForm, ...option }: CheckoutCarRentalServiceOption & {showEquipmentsForm: boolean, setShowEquipmentsForm: (value: boolean) => void;  }) => {
  const { service, setServiceValue } = useService(ServiceType.CarRentalFiltering);
  const [isFixed, setIsFixed] = React.useState(false)
  const intl = useIntl();
  const contentWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const {sendEvent} = useAnalytics();
  const { endEditingCar, toggleEquipmentEnquiry  } = useCarRentalServiceStore()
  const {label, value: carValue, id: carId, driverId} = option;
  const driver = driverId ? service?.participants[driverId] : null;
  const currentEditingCar = useCarRentalServiceStore(editingCar(carId))
  const [totalEquipmentPrice, setTotalEquipmentPrice] = React.useState(getTotalPriceForEquipmentsSelected(option) ?? 0);

  const {
    bookedEquipmentsWithQuantity,
  } = currentEditingCar


  React.useEffect(() => {
    const totalPrice = bookedEquipmentsWithQuantity?.reduce((acc, {id, quantity}) => {
      const equipment = option.equipments.find(({id: equipmentId}) => equipmentId === id);
      if (!equipment) return acc;
      const price = (equipment.price ?? 0) * quantity;
      return acc + price;
    }, 0);
    setTotalEquipmentPrice(totalPrice ?? 0);

    const checkHeight = () => {
      const contentHeight = contentWrapperRef.current?.scrollHeight || 0;
      const clientHeight = contentWrapperRef.current?.clientHeight || 0;
      setIsFixed(contentHeight > clientHeight);
    };

    const resizeObserver = new ResizeObserver(() => {
      checkHeight();
    });

    if (contentWrapperRef.current) {
      resizeObserver.observe(contentWrapperRef.current);
    }

    if (showEquipmentsForm) {
      setTimeout(() => {
        checkHeight();
      }, 0);
    }
    window.addEventListener('resize', checkHeight);

    return () => {
      window.removeEventListener('resize', checkHeight);

      if (contentWrapperRef.current) {
        resizeObserver.unobserve(contentWrapperRef.current);
      }
    };


  }, [showEquipmentsForm, option, bookedEquipmentsWithQuantity, contentWrapperRef, driverId, carId, service?.participants]);


  const handleCloseButton = () => {
    endEditingCar(option.value);
    setShowEquipmentsForm(false);
  }


  const handleEquipmentsQuantityChange = (value: string, quantity: number) => {
    toggleEquipmentEnquiry(carId, value, quantity);

    sendEvent(
      createEquipmentSelectionEvent({
        result: bookedEquipmentsWithQuantity.find(({id}) => id === value) ? 'equipment_remove' : 'equipment_add',
        type: carValue,
        description: availableEquipments.find((equipment) => equipment.id === value)?.text ?? '',
        element: 'input stepper',
        validated: true
      })
    )
  };

  const handleEquipmentsSwitchChange = (value: string, quantity: number) => {
    toggleEquipmentEnquiry(carId, value, quantity);
    sendEvent(
      createEquipmentSelectionEvent({
        result: bookedEquipmentsWithQuantity.find(({id}) => id === value) ? 'equipment_remove' : 'equipment_add',
        type: carValue,
        description: availableEquipments.find((equipment) => equipment.id === value)?.text ?? '',
        element: 'toggle',
        validated: true
      })
    )
  };

  const handleSaveValue =
    () => {
      if (!service) return;
      if (!driver) return;
      setServiceValue(editCar({
            value: carValue,
            id: carId,
            participantId: driverId ?? '',
            driverBirthDate: driver?.birthdate ? new Date(driver.birthdate) : new Date(),
            bookedEquipmentIds: bookedEquipmentsWithQuantity?.map(({id}) => id) ?? [],
            bookedEquipmentsWithQuantity: bookedEquipmentsWithQuantity ?? [],
          })(service));

        endEditingCar(carId);
        setShowEquipmentsForm(false);

        sendEvent(
          createCarEvent({
            result: 'Car equipments selected',
            type: carValue,
            description: 'selectEquipments',
            element: 'button',
            validated: true
          })
        )
    }

  if (!service) return null;
  if (!option) return null;
  const availableEquipments = getOptionEquipments(option, bookedEquipmentsWithQuantity);
  const lastEligibleId = availableEquipments?.filter(e => e.isElegible).at(-1)?.id;
  const {equipments, maxNumberOfEquipments} = option
  const equipmentsLimit = maxNumberOfEquipments ?? equipments.length

  return <>
    <SheetHeader>
      <SheetClose onClick={handleCloseButton}>{intl.formatMessage({id: 'close'})}</SheetClose>
    </SheetHeader>
    <EquipmentsFormWrapper ref={contentWrapperRef}>
        <SpaceBetween direction="y" spacing={space('primitives_space_8')}>
          <CarListName $variant="heading-md">{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_car_model'}, {carModel: label})} </CarListName>
          <Typography $variant="body-sm">
            {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_select_extras_description'}, {max: option?.maxNumberOfEquipments})}
          </Typography>
          <SpaceBetween direction="y" spacing={space('primitives_space_8')}>
            {availableEquipments && availableEquipments.length > 0 && (
              <SpaceBetween direction="y" spacing={space('primitives_space_8')}>
                <EquipmentsFormField>
                  { availableEquipments?.map(({id, text, selected, price, maxQuantity, quantitySelected, isElegible}) => {
                    const selectedEquipmentsLength = availableEquipments.filter(e => e.selected).length;
                    const isLastEligibleWithMaxReached =
                      isElegible &&
                      id === lastEligibleId &&
                      equipmentsLimit &&
                      selectedEquipmentsLength >= equipmentsLimit;
                    return (
                        <SpaceBetween key={id} direction="y" spacing={space('primitives_space_8')}>

                          <EquipmentField disabled={!isElegible} as='div'>
                            <ListItem.Content>
                              <ListItem.Label>{text}{price ? `(${intl.formatCurrency(price)})` : ''}</ListItem.Label>
                            </ListItem.Content>
                            <ListItem.Trailing>
                              {maxQuantity && maxQuantity > 1 && (
                                <InputStepper
                                  minimumValue={0}
                                  maximumValue={maxQuantity}
                                  initialValue={quantitySelected}
                                  disabled={!isElegible}
                                  size="M"
                                  onChange={(value) => handleEquipmentsQuantityChange(id, value)}
                                  decrementButtonAriaLabel={intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_equipments_decrement_button_aria_label'}, {equipmentName: text})}
                                  incrementButtonAriaLabel={intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_equipments_increment_button_aria_label'}, {equipmentName: text})}
                                  valueAriaLabel={intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_equipments_value_aria_label'}, {equipmentName: text})}
                                />
                              )}
                              {!maxQuantity || maxQuantity === 1 && (
                                <Switch defaultChecked={selected}
                                        value={id}
                                        disabled={!isElegible}
                                        onCheckedChange={(value) => handleEquipmentsSwitchChange(id, value ? 1 : 0)}/>
                              )}

                            </ListItem.Trailing>
                          </EquipmentField>
                          {isLastEligibleWithMaxReached && (
                            <EquipmentsLimitReachedBanner
                              $type="neutral-dark">
                              <SpaceBetween direction="x" spacing={space('primitives_space_8')}>
                                <Icon glyph="InformationInCircle" size={32}/>
                                <SpaceBetween direction='y' spacing={space('primitives_space_4')}>
                                  <Typography $variant="body-md-bold">{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_max_banner_text'})}</Typography>
                                  <Typography $variant="body-sm">{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_max_banner_supporting_text'}, {limit: equipmentsLimit})}</Typography>
                                </SpaceBetween>
                              </SpaceBetween>
                            </EquipmentsLimitReachedBanner>
                          )}

                        </SpaceBetween>
                      );
                    }
                  )}
                </EquipmentsFormField>
              </SpaceBetween>
            )}
          </SpaceBetween>
          <TotalEquipmentsPrice>
            <ListItem.Content>
              <TotalEquipmentsPriceText>
                {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_total_price'})}
              </TotalEquipmentsPriceText>
              <TotalEquipmentsPriceHintText>
                {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_total_price_hint_text'})}
              </TotalEquipmentsPriceHintText>
            </ListItem.Content>
            <ListItem.Trailing>
              <Typography $variant="body-lg-bold">{intl.formatCurrency(totalEquipmentPrice)}</Typography>
            </ListItem.Trailing>
          </TotalEquipmentsPrice>
        </SpaceBetween>
    </EquipmentsFormWrapper>
    <SheetFooter $isFixed={isFixed}>
      <SpaceBetween direction="x" spacing={space('primitives_space_8')} justifyContent='end'>
        <Button $hierarchy="primary" $fullSize onClick={handleSaveValue}>
          {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_save_button_text'})}
        </Button>
      </SpaceBetween>
    </SheetFooter>
  </>
}

const CarListName = styled(Typography)`
    color: ${tok('semantics_text_color')};
`
const EquipmentsFormWrapper = styled(SheetContentWrapper)`
  padding: ${space('primitives_space_16')};
`
const EquipmentsFormField = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${space('primitives_space_8')};
`;
const EquipmentField = styled(ListItem.Root)`
`;
const TotalEquipmentsPrice = styled(ListItem.Root)`
  background-color: ${tok('semantics_backgrounds_tinted_background_color')};
  padding: ${space('primitives_space_12')};
  border: none;

  ${ListItem.Trailing} {
    align-self: flex-start;
  }
`;
const TotalEquipmentsPriceText = styled(ListItem.Label)`
  ${Typography.modVariant('body-lg-bold')};
  color: ${tok('semantics_text_color')};
`
const TotalEquipmentsPriceHintText = styled(ListItem.Description)`
  color: ${tok('semantics_text_secondary_color')};;
`
const EquipmentsLimitReachedBanner = styled(Banner)`
  ${Icon} {
    color: ${tok('primitives_color_neutral_600')};
  }
  ${Typography} {
    color: ${tok('semantics_text_color')};
  }
`
